import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, ShieldAlert, Trash2, KeyRound, Search, Copy, Check } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";

const ROLE_OPTS = [
  { v: "organizer", l: "Organizer" },
  { v: "player", l: "Player" },
  { v: "admin", l: "Admin" },
  { v: "super_admin", l: "Super Admin" },
];
const TIER_OPTS = [
  { v: "free", l: "Free Tier" },
  { v: "club_pro", l: "Club Pro License" },
  { v: "umpire", l: "Umpire" },
];
const ROLE_FILTERS = [
  { v: "all", l: "All roles" },
  { v: "organizer", l: "Organizers" },
  { v: "admin", l: "Admins" },
  { v: "super_admin", l: "Super Admins" },
];

function selectClass() {
  return "rounded-lg bg-surface glow-border px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-lime-400";
}

function UserRow({ u, tierOpts, onSaved, onDelete, onReset }) {
  const [role, setRole] = useState(u.role || "organizer");
  const [tier, setTier] = useState(u.license_tier || "free");
  const [expires, setExpires] = useState(u.license_expires || "");
  const [busy, setBusy] = useState(false);

  const dirty = role !== (u.role || "organizer") || tier !== (u.license_tier || "free") || expires !== (u.license_expires || "");
  const isSuper = u.role === "super_admin";

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/users/${u.user_id}`, { role, license_tier: tier, license_expires: expires || "" });
      toast.success("User updated");
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr data-testid={`user-row-${u.user_id}`} className="border-b" style={{ borderColor: "var(--border)" }}>
      <td className="py-3 pr-3 pl-4">
        <div className="font-semibold text-sm truncate max-w-[180px] flex items-center gap-1.5">
          {isSuper && <ShieldAlert className="w-3.5 h-3.5 text-primary-c shrink-0" />}
          {u.name || "—"}
        </div>
        <div className="text-xs mono truncate max-w-[200px]" style={{ color: "var(--text-2)" }}>{u.email}</div>
      </td>
      <td className="py-3 pr-3">
        <select data-testid={`user-role-${u.user_id}`} value={role} onChange={(e) => setRole(e.target.value)} className={selectClass()} style={{ color: "var(--text)" }}>
          {ROLE_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      </td>
      <td className="py-3 pr-3">
        <select data-testid={`user-tier-${u.user_id}`} value={tier} onChange={(e) => setTier(e.target.value)} className={selectClass()} style={{ color: "var(--text)" }}>
          {(tierOpts || TIER_OPTS).map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      </td>
      <td className="py-3 pr-3">
        <input data-testid={`user-expires-${u.user_id}`} type="date" value={expires || ""} onChange={(e) => setExpires(e.target.value)}
          className={selectClass()} style={{ color: "var(--text)" }} />
      </td>
      <td className="py-3 pr-4">
        <div className="flex items-center gap-1.5 justify-end">
          <button data-testid={`user-save-${u.user_id}`} onClick={save} disabled={!dirty || busy}
            className="btn-glow rounded-lg px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 disabled:opacity-40">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
          </button>
          <button data-testid={`user-reset-${u.user_id}`} onClick={() => onReset(u)} aria-label="Reset password"
            className="glass rounded-lg w-8 h-8 flex items-center justify-center hover:scale-105 transition-transform">
            <KeyRound className="w-3.5 h-3.5 text-primary-c" />
          </button>
          <button data-testid={`user-delete-${u.user_id}`} onClick={() => onDelete(u)} disabled={isSuper} aria-label="Delete user"
            className="glass rounded-lg w-8 h-8 flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-30">
            <Trash2 className="w-3.5 h-3.5" style={{ color: "#ef4444" }} />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [tierOpts, setTierOpts] = useState(TIER_OPTS);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/admin/users").then(({ data }) => setUsers(data || [])).catch(() => setUsers(false));
    api.get("/config/tiers").then(({ data }) => setTierOpts((data.tiers || []).map((t) => ({ v: t.id, l: t.name })))).catch(() => {});
  }, []);

  const onSaved = (updated) => setUsers((prev) => prev.map((u) => (u.user_id === updated.user_id ? { ...u, ...updated } : u)));

  const filtered = useMemo(() => {
    if (!Array.isArray(users)) return [];
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== "all" && (u.role || "organizer") !== roleFilter) return false;
      if (!q) return true;
      return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
    });
  }, [users, query, roleFilter]);

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${toDelete.user_id}`);
      setUsers((prev) => prev.filter((u) => u.user_id !== toDelete.user_id));
      toast.success("User deleted");
      setToDelete(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const doReset = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/reset-password`);
      setResetResult(data);
      setCopied(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reset failed");
    }
  };

  const copyTemp = () => {
    navigator.clipboard?.writeText(resetResult?.temp_password || "");
    setCopied(true);
    toast.success("Temporary password copied");
  };

  if (users === null) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>;
  if (users === false) return <p className="text-sm" style={{ color: "var(--text-2)" }}>Could not load users.</p>;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-c" />
          <input data-testid="user-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name or email…"
            className="w-full rounded-full bg-surface glow-border pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-lime-400" style={{ color: "var(--text)" }} />
        </div>
        <div className="glass rounded-full p-0.5 flex gap-0.5" data-testid="user-role-filter">
          {ROLE_FILTERS.map((r) => (
            <button key={r.v} data-testid={`user-filter-${r.v}`} onClick={() => setRoleFilter(r.v)}
              className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${roleFilter === r.v ? "text-[var(--bg)]" : ""}`}
              style={roleFilter === r.v ? { background: "var(--primary)" } : { color: "var(--text-2)" }}>{r.l}</button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl glow-border overflow-x-auto" style={{ background: "var(--surface)" }} data-testid="admin-users-table">
        <table className="w-full min-w-[720px] text-left">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider mono" style={{ color: "var(--text-2)" }}>
              <th className="py-3 px-4">Organizer / Host</th>
              <th className="py-3 pr-3">Role</th>
              <th className="py-3 pr-3">License</th>
              <th className="py-3 pr-3">Expires</th>
              <th className="py-3 pr-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <UserRow key={u.user_id} u={u} tierOpts={tierOpts} onSaved={onSaved} onDelete={setToDelete} onReset={doReset} />
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="p-6 text-sm text-center" style={{ color: "var(--text-2)" }}>No users match your filters.</p>}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="glass" style={{ background: "var(--surface-glass)" }} data-testid="user-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display flex items-center gap-2"><Trash2 className="w-5 h-5" style={{ color: "#ef4444" }} /> Delete user?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: "var(--text-2)" }}>
              This permanently removes <span className="font-bold">{toDelete?.name || toDelete?.email}</span>, their published events and message history. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="user-delete-confirm" onClick={(e) => { e.preventDefault(); confirmDelete(); }} disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)}>
        <DialogContent className="glass" style={{ background: "var(--surface-glass)" }} data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><KeyRound className="w-5 h-5 text-primary-c" /> Temporary password</DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>
              Share this one-time password with <span className="font-bold">{resetResult?.email}</span>. They should change it after signing in.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <input readOnly data-testid="reset-temp-password" value={resetResult?.temp_password || ""}
              className="flex-1 rounded-lg bg-surface glow-border px-3 py-2.5 text-sm mono tracking-wider" style={{ color: "var(--text)" }} />
            <button data-testid="reset-copy" onClick={copyTemp} className="btn-glow rounded-lg px-3 py-2.5 font-bold flex items-center gap-1 shrink-0">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
